import { useEffect, useState, type FormEvent } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Francisco } from '@/components/francisco/Francisco'
import { ApiError, LOGIN_URL, panelApi, request } from '@/lib/api'
import { ctaButton, secondaryButton } from './StatusScreens'

/* No session (GET /me → 401): sign in. GET /auth/options says how: e-mail and password (PANEL_LOGIN_*), the ChildFund
   Microsoft account (Entra), or, in dev with neither set up, straight in.
   ?denied=1 (or 403): the account is not on the panel list. ?login_error=1: the sign-in failed. */

const inputClass =
    'box-border min-h-[40px] w-full rounded-[10px] border border-line bg-bg px-3 text-[14px] text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green'

function passwordErrorText(error: unknown): string {
    if (error instanceof ApiError && error.code === 'invalid_credentials') return 'E-mail ou senha incorretos.'
    if (error instanceof ApiError && error.code === 'limit_reached') return 'Muitas tentativas erradas. Espere 15 minutos e tente de novo.'
    if (error instanceof ApiError && error.code === 'forbidden') return 'Este acesso não está liberado no painel.'
    return 'Não foi possível entrar agora. Tente de novo.'
}

/** Sai da sessão atual e volta ao login (com senha, o formulário; só com Microsoft, o login da Microsoft). */
function SwitchAccountButton({ microsoftOnly }: { microsoftOnly: boolean }) {
    const [busy, setBusy] = useState(false)
    const go = async () => {
        setBusy(true)
        try {
            await panelApi.logout()
        } catch {
            // sem sessão para encerrar: segue para o login mesmo assim
        }
        window.location.replace(microsoftOnly ? LOGIN_URL : '/')
    }
    return <button type="button" disabled={busy} onClick={() => void go()} className={`${secondaryButton} mt-1 w-full disabled:opacity-70`}>Entrar com outra conta</button>
}

/** E-mail and password form; on success the page reloads into the panel. */
function PasswordForm() {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const login = useMutation({
        mutationFn: () => panelApi.passwordLogin({ email, password }),
        onSuccess: () => window.location.replace('/staff'),
        onError: () => setPassword(''),
    })
    const onSubmit = (e: FormEvent) => {
        e.preventDefault()
        if (email.trim() && password && !login.isPending) login.mutate()
    }
    return (
        <form onSubmit={onSubmit} className="mt-1 flex w-full flex-col gap-2.5 text-left" aria-label="Entrar com e-mail e senha">
            <label className="flex flex-col gap-1 text-[13px] font-semibold">
                E-mail
                <input type="email" autoComplete="username" required value={email} onChange={e => setEmail(e.target.value)} className={inputClass} />
            </label>
            <label className="flex flex-col gap-1 text-[13px] font-semibold">
                Senha
                <input type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} className={inputClass} />
            </label>
            {login.isError && (
                <p role="alert" className="m-0 rounded-[10px] px-3 py-2 text-[13px] font-medium text-red-ink" style={{ background: 'var(--red-bg)' }}>
                    {passwordErrorText(login.error)}
                </p>
            )}
            <button type="submit" disabled={login.isPending} className={`${ctaButton} mt-1 w-full disabled:cursor-wait disabled:opacity-70`}>
                {login.isPending ? 'Entrando…' : 'Entrar'}
            </button>
            <p className="m-0 text-center text-[12.5px] text-muted">Primeiro acesso ou esqueceu a senha? Peça ao administrador um link de acesso.</p>
        </form>
    )
}

function MicrosoftMark() {
    return (
        <svg width="14" height="14" viewBox="0 0 21 21" aria-hidden="true">
            <rect x="0" y="0" width="10" height="10" fill="currentColor" opacity=".95" />
            <rect x="11" y="0" width="10" height="10" fill="currentColor" opacity=".75" />
            <rect x="0" y="11" width="10" height="10" fill="currentColor" opacity=".75" />
            <rect x="11" y="11" width="10" height="10" fill="currentColor" opacity=".55" />
        </svg>
    )
}

export function LoginScreen({ denied = false, failed = false }: { denied?: boolean; failed?: boolean }) {
    const options = useQuery({ queryKey: ['panel', 'auth', 'options'], queryFn: panelApi.authOptions, retry: false, staleTime: Infinity })
    const password = options.data?.password ?? false
    // Old server without /auth/options, or it failed: keep the Microsoft button (the server decides on click)
    const microsoft = options.data ? options.data.microsoft : options.isError
    const dev = options.data?.dev ?? false
    return (
        <main className="grid min-h-dvh place-items-center p-4">
            <section
                aria-labelledby="login-title"
                className="flex w-full max-w-[400px] flex-col items-center gap-3 rounded-[14px] border border-line bg-panel px-6 pt-6 pb-5 text-center"
            >
                <span className="grid place-items-center rounded-full p-2" style={{ background: 'color-mix(in srgb, var(--rbg) 14%, transparent)' }}>
                    <Francisco color="g" mood={denied ? 'confused' : 'rest'} size={84} label={denied ? 'Francisco, confuso' : 'Francisco, o agente'} />
                </span>
                {denied ? (
                    <>
                        <h1 id="login-title" className="m-0 text-[24px] leading-tight font-bold">Você não tem acesso a este painel</h1>
                        <p className="m-0 text-[14px] leading-normal text-muted">
                            Este e-mail não está liberado no painel. Se você precisa de acesso, peça ao administrador do painel.
                        </p>
                        <SwitchAccountButton microsoftOnly={!password && microsoft} />
                    </>
                ) : (
                    <>
                        <h1 id="login-title" className="m-0 text-[24px] leading-tight font-bold">Painel ChildFund</h1>
                        <p className="m-0 text-[14px] leading-normal text-muted">
                            {password ? 'Entre com o seu e-mail e senha' : 'Entre com a sua conta da ChildFund'} para ver os agentes, o que precisa de você e os sistemas.
                        </p>
                        {failed && (
                            <p role="alert" className="m-0 w-full rounded-[10px] px-3 py-2 text-[13px] font-medium text-red-ink" style={{ background: 'var(--red-bg)' }}>
                                Não foi possível entrar. Tente de novo.
                            </p>
                        )}
                        {password && <PasswordForm />}
                        {microsoft && (
                            <a href={LOGIN_URL} className={`${password ? secondaryButton : ctaButton} mt-1 w-full`}>
                                <MicrosoftMark />
                                Entrar com Microsoft
                            </a>
                        )}
                        {dev && (
                            <a href={LOGIN_URL} className={`${ctaButton} mt-1 w-full`}>
                                Entrar (desenvolvimento)
                            </a>
                        )}
                        {options.isPending && <span className="text-[13px] text-muted">Carregando…</span>}
                    </>
                )}
            </section>
        </main>
    )
}

/* /definir-senha#<código> — sem sessão: a pessoa abre o link de acesso que o admin mandou (uso único, 72 h), cria a
   senha e já entra no painel. O código fica depois do # (não vai para o servidor pela URL) e segue no corpo. */
export function SetPasswordScreen() {
    const [token] = useState(() => window.location.hash.replace(/^#/, ''))
    const [password, setPassword] = useState('')
    const [confirm, setConfirm] = useState('')
    const who = useQuery({
        queryKey: ['panel', 'auth', 'access', token],
        queryFn: () => request<{ name: string; email: string; hasPassword: boolean }>('POST', '/auth/access/check', { token }),
        enabled: token.length > 0,
        retry: false,
    })
    // Tira o código da barra de endereço assim que a tela abre
    useEffect(() => {
        if (window.location.hash) window.history.replaceState(null, '', window.location.pathname)
    }, [])
    const save = useMutation({
        mutationFn: () => request<void>('POST', '/auth/access/use', { token, password }),
        onSuccess: () => window.location.replace('/staff'),
    })
    const tooShort = password.length > 0 && password.length < 10
    const mismatch = confirm.length > 0 && confirm !== password
    const canSave = password.length >= 10 && password === confirm && !save.isPending
    const onSubmit = (e: FormEvent) => {
        e.preventDefault()
        if (canSave) save.mutate()
    }
    const invalid = !token || who.isError
    return (
        <main className="grid min-h-dvh place-items-center p-4">
            <section aria-labelledby="pwd-title" className="flex w-full max-w-[400px] flex-col items-center gap-3 rounded-[14px] border border-line bg-panel px-6 pt-6 pb-5 text-center">
                <span className="grid place-items-center rounded-full p-2" style={{ background: 'color-mix(in srgb, var(--rbg) 14%, transparent)' }}>
                    <Francisco color="g" mood={invalid ? 'confused' : 'rest'} size={84} label="Francisco, o agente" />
                </span>
                {invalid ? (
                    <>
                        <h1 id="pwd-title" className="m-0 text-[24px] leading-tight font-bold">Link inválido ou vencido</h1>
                        <p className="m-0 text-[14px] leading-normal text-muted">Cada link vale uma vez só, por 72 horas. Peça ao administrador do painel um link novo.</p>
                        <a href="/" className={`${secondaryButton} mt-1 w-full`}>Ir para o login</a>
                    </>
                ) : who.isPending ? (
                    <span className="text-[13px] text-muted">Conferindo o link…</span>
                ) : (
                    <>
                        <h1 id="pwd-title" className="m-0 text-[24px] leading-tight font-bold">{who.data?.hasPassword ? 'Troque a sua senha' : 'Crie a sua senha'}</h1>
                        <p className="m-0 text-[14px] leading-normal text-muted">Olá, {who.data?.name.split(' ')[0]}. Você vai entrar no painel com <b className="text-fg">{who.data?.email}</b> e a senha que criar aqui.</p>
                        <form onSubmit={onSubmit} className="mt-1 flex w-full flex-col gap-2.5 text-left" aria-label="Criar senha">
                            <input type="email" autoComplete="username" value={who.data?.email ?? ''} readOnly hidden />
                            <label className="flex flex-col gap-1 text-[13px] font-semibold">
                                Nova senha
                                <input type="password" autoComplete="new-password" required minLength={10} value={password} onChange={e => setPassword(e.target.value)} className={inputClass} aria-describedby="pwd-hint" />
                            </label>
                            <span id="pwd-hint" className={`text-[12.5px] ${tooShort ? 'text-red-ink' : 'text-muted'}`}>Pelo menos 10 caracteres. Uma frase curta é mais fácil de lembrar e mais segura.</span>
                            <label className="flex flex-col gap-1 text-[13px] font-semibold">
                                Repita a senha
                                <input type="password" autoComplete="new-password" required value={confirm} onChange={e => setConfirm(e.target.value)} className={inputClass} />
                            </label>
                            {mismatch && <span className="text-[12.5px] text-red-ink">As duas senhas não são iguais.</span>}
                            {save.isError && (
                                <p role="alert" className="m-0 rounded-[10px] px-3 py-2 text-[13px] font-medium text-red-ink" style={{ background: 'var(--red-bg)' }}>
                                    {save.error instanceof ApiError && save.error.code === 'not_found' ? 'Este link já foi usado ou venceu. Peça um novo ao administrador.' : 'Não deu para salvar a senha agora. Tente de novo.'}
                                </p>
                            )}
                            <button type="submit" disabled={!canSave} className={`${ctaButton} mt-1 w-full disabled:cursor-not-allowed disabled:opacity-60`}>
                                {save.isPending ? 'Salvando…' : 'Salvar senha e entrar'}
                            </button>
                        </form>
                    </>
                )}
            </section>
        </main>
    )
}
